import { useState } from "react";

import { Checkbox } from "@newsela/angelou";

import { withTrackEvent, actionNames, objectTypes } from "@newsela/snitch";

import {
  handleDefaultReadingLevelChange,
  handleReadingLevelLockChange,
  handleHideLexileLevel,
} from "@/assignments/utils/assignmentActions";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import LevelDropdown from "@/view/LevelSelection/LevelDropdown/LevelDropdown";
import LevelToolTipDialog from "@/view/LevelSelection/LevelToolTipDialog";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import AssignmentToggleButton from "../AssignmentToggleButton";
import {
  asteriskBlue,
  checkBoxAndTooltipContainerCss,
  dropdownCss,
  sectionCss,
  rowCss,
  toggleButtonCss,
  registeredTrademarkCss,
  lexileLevelLabelWrapperCss,
} from "./LexileLevelSettings.style";

export interface LexileLevelProps {
  currentLevel: ArticleLevel;
  levels: ArticleLevel[];
  setContentLevel: (arg: ArticleLevel) => void;
}
const LexileLevel = ({
  currentLevel,
  levels,
  setContentLevel,
}: LexileLevelProps) => {
  const { updatedAssignment, dispatch } = useAssignmentReducerContext() as {
    updatedAssignment: Assignment;
    dispatch: () => void;
  };

  const isLexileLevelHidden =
    updatedAssignment?.assignment_config?.hide_lexile_level ?? false;

  const [isHideLevelChecked, setIsHideLevelChecked] =
    useState(isLexileLevelHidden);

  const isReadingLevelLocked = updatedAssignment?.is_reading_level_locked;

  const onReadingLevelLockChange = (isReadingLevelLocked: boolean) => {
    handleReadingLevelLockChange(isReadingLevelLocked, dispatch);
  };

  const onHideLexileLevelChange = (isLexileLevelHidden: boolean) =>
    handleHideLexileLevel({ hide_lexile_level: isLexileLevelHidden }, dispatch);
  const handleHideLevelClick = () => {
    setIsHideLevelChecked(
      (prevIsHideLevelChecked: boolean) => !prevIsHideLevelChecked,
    );
    onHideLexileLevelChange(!isLexileLevelHidden);
  };

  const handleLockLevelClick = () => {
    onReadingLevelLockChange(!isReadingLevelLocked);

    if (isHideLevelChecked) {
      handleHideLevelClick();
    }
  };

  const eventProperties = {
    objectType: objectTypes.assignment,
    actionName: actionNames.update,
    streamProperties: {
      content_type: "Article",
      content_id: currentLevel?.id,
      content_ids: [currentLevel?.header_id],
      object_id: updatedAssignment?.content_id || null,
    },
  };

  const toggleEventProperties = {
    ...eventProperties,
    streamProperties: {
      ...eventProperties.streamProperties,
      description: `Toggle allow students to change level checkbox ${
        isReadingLevelLocked ? "on" : "off"
      }`,
    },
  };

  const checkboxEventProperties = {
    ...eventProperties,
    streamProperties: {
      ...eventProperties.streamProperties,
      description: `Toggle hide lexile level from students ${isLexileLevelHidden ? "on" : "off"}`,
    },
  };

  const CheckboxWithTrackEvent = withTrackEvent(
    Checkbox,
  ) as React.ForwardRefExoticComponent<
    React.ComponentProps<typeof Checkbox> &
      React.RefAttributes<HTMLInputElement>
  >;

  return (
    <div css={sectionCss}>
      <div css={lexileLevelLabelWrapperCss}>
        <h2>Lexile </h2>
        <span css={registeredTrademarkCss}>®</span>
        <span css={asteriskBlue}>*</span>
        <LevelToolTipDialog
          assignmentId={updatedAssignment?.content_id || null}
          currentContentLevelId={currentLevel?.id}
        />
      </div>
      <div css={rowCss}>
        <div css={dropdownCss}>
          <LevelDropdown
            currentContentLevel={currentLevel}
            defaultReadingLevel={updatedAssignment?.default_reading_level}
            eventProperties={eventProperties}
            language={currentLevel?.language}
            levels={levels}
            onDefaultReadingLevelChange={(defaultReadingLevel) =>
              handleDefaultReadingLevelChange(defaultReadingLevel, dispatch)
            }
            setContentLevel={setContentLevel}
          />
        </div>
        <div css={toggleButtonCss}>
          <AssignmentToggleButton
            dataProps={{ "data-qa-selector": "assignment-level-toggle" }}
            handleClick={() => {
              handleLockLevelClick();
            }}
            isPressed={!isReadingLevelLocked}
            label="Students can change level"
            toggleEventProperties={toggleEventProperties}
          />
        </div>

        <div css={checkBoxAndTooltipContainerCss}>
          <CheckboxWithTrackEvent
            checked={isHideLevelChecked}
            disabled={!isReadingLevelLocked}
            label="Hide level from students"
            name="Hide level from students"
            dataProps={{
              "data-qa-selector": `hide_lexile_level_from_students_${
                isLexileLevelHidden ? "on" : "off"
              }`,
            }}
            onClick={() => {
              handleHideLevelClick();
            }}
            {...checkboxEventProperties}
          />
        </div>
      </div>
    </div>
  );
};

export default LexileLevel;
